"use client";

import React, { createContext, useContext } from "react";
import { CartProvider } from "@/hooks/use-cart"; // This is our Logic Provider
import { PageContext } from "@/components/ecoBuilder/renderer/PageContext";
import { WhatsAppWidget } from "@/components/storefront/WhatsAppWidget";
import { NextIntlClientProvider } from 'next-intl';

// ═══════════════════════════════════════════════════════════════
// Site Context - Provides site info to all tenant pages
// ═══════════════════════════════════════════════════════════════
interface SiteContextValue {
    siteId: string | null;
    siteSlug: string | null;
    siteName: string | null;
}

const SiteContext = createContext<SiteContextValue>({
    siteId: null,
    siteSlug: null,
    siteName: null,
});

export const useSiteContext = () => useContext(SiteContext);

// ═══════════════════════════════════════════════════════════════
// Tenant Layout Wrapper - Client-side cart functionality
// ═══════════════════════════════════════════════════════════════
interface TenantLayoutWrapperProps {
    children: React.ReactNode;
    /**
     * Complete site data passed from server layout
     * Includes: id, slug, theme_mode, active_system_theme
     */
    siteData: any;
    messages?: any;
}

import { FontLoader } from "@/components/store/FontLoader";

export function TenantLayoutWrapper({
    children,
    siteData,
    messages = {},
}: TenantLayoutWrapperProps) {
    const siteId = siteData?.id ?? null;
    const siteSlug = siteData?.slug ?? null;
    const siteName = siteData?.name ?? null;

    const themeSettings = siteData?.theme_settings ?? {};

    // Explicit Check for System Theme Mode
    const isSystemTheme = siteData?.theme_mode === 'system';

    // Extract font family securely
    const fontFamily = themeSettings.fontFamily || 'Inter';

    // Debugging
    if (process.env.NODE_ENV === 'development') {
        console.log('[TenantLayoutWrapper] Initializing with:', {
            siteId,
            siteSlug,
            siteName,
            theme_mode: siteData?.theme_mode,
            isSystemTheme,
            themeSettingsKeys: Object.keys(themeSettings || {})
        });
    }

    // Use siteSlug for cart isolation, fallback to siteId or 'default'
    const cartSiteKey = siteSlug || siteId || "default";

    // Extract marketing settings
    const marketingSettings = siteData?.integrations || {};
    const whatsappSettings = marketingSettings?.whatsapp;

    // ------------------------------------------------------------------
    // MODE 1: SYSTEM THEME (Full Store)
    // ------------------------------------------------------------------
    // Needs Global Cart State & Context
    if (isSystemTheme) {
        return (
            <NextIntlClientProvider locale="ar" messages={messages} timeZone="UTC">
                <PageContext.Provider value={{ themeSettings, siteId }}>
                    <SiteContext.Provider value={{ siteId, siteSlug, siteName }}>
                        <FontLoader fontFamily={fontFamily} />
                        <CartProvider siteSlug={cartSiteKey}>
                            {children}
                            <WhatsAppWidget settings={whatsappSettings} />
                        </CartProvider>
                    </SiteContext.Provider>
                </PageContext.Provider>
            </NextIntlClientProvider>
        );
    }

    // ------------------------------------------------------------------
    // MODE 2: BUILDER (Landing Page Only)
    // ------------------------------------------------------------------
    // No Global Cart.
    // Order Forms are embedded directly in the page content.
    return (
        <PageContext.Provider value={{ themeSettings, siteId }}>
            <SiteContext.Provider value={{ siteId, siteSlug, siteName }}>
                <FontLoader fontFamily={fontFamily} />
                {children}
                <WhatsAppWidget settings={whatsappSettings} />
            </SiteContext.Provider>
        </PageContext.Provider>
    );
}

export default TenantLayoutWrapper;
